import React from 'react'
import { ListItem, ListItemButton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import CustomTooltip from '../../../../components/CustomTooltip'
import { useUserRole } from '../../../../hooks/useUserRole'
import { ROLES } from '../../../../consts/roles'
import { type SurveyCompletionRate } from '../../../../models/Division'
import { SlotStatus } from '../../../../models/DistributionPlan'
import { GlobalSettingsManager } from '../../../../APIManager'
import { type ReportActionKind } from '../reportActionCopy'

const REGENERATE_DISABLED_TOOLTIP = "Can't regenerate while this section is still collecting responses."
// Mirrors SectionTable's own freshness-hint query (same queryKey, same staleTime) so the two
// share one cache entry when both are mounted on the same page.
const GLOBAL_SETTINGS_STALE_TIME_MS = 5 * 60 * 1000

interface ReportActionsMenuProps {
  // A row was picked. The caller closes the menu and opens the confirmation itself: the menu
  // unmounts its rows once it closes, so a modal rendered from here would unmount with them.
  onSelect: (action: ReportActionKind) => void
  // The current report's completion-rate row — source of the target name, survey project / term
  // names, and soft-delete version metadata. The revert/restore items only appear when real
  // version history exists (currently never, since the backend does not archive versions yet).
  meta?: SurveyCompletionRate | null
  // True when the current report scope is a Course Section (vs an Academic Unit).
  isSection?: boolean
  // Section row status carried from the CDG children-list row. The single-object
  // completion-rate endpoint does not currently include slotStatus.
  sectionSlotStatus?: SlotStatus | null
}

/**
 * The Account-Owner rows of the reports ⋯ — "Regenerate Report" and, when version history exists,
 * "Revert to previous version" / "Restore latest version".
 *
 * Rows, not a menu of its own. It carried its own ⋯ button until 2026-09-23, which put two
 * unlabelled ⋯ side by side once the display-settings menu started rendering for Global-role
 * holders. `ReportsControls` owns the single button now and renders these inside it, and owns the
 * confirmation modal too, outside the popover (see `onSelect`).
 */
const ReportActionsMenu: React.FC<ReportActionsMenuProps> = ({ onSelect, meta, isSection = false, sectionSlotStatus = null }) => {
  const { activeRole } = useUserRole()

  const isAccountOwner = activeRole?.roleName === ROLES.ACCOUNT_OWNER
  // CR-271: the single-object completion-rate endpoint may not include slotStatus, so prefer the
  // status threaded from the selected SectionTable CDG row and fall back to meta for future BE parity.
  const resolvedSlotStatus = sectionSlotStatus ?? meta?.slotStatus
  const isLiveSection = isSection && resolvedSlotStatus === SlotStatus.Live

  // CR-271: Account Owner's Regenerate-while-LIVE block is lifted at institutions with
  // is_live_reports_enabled on. Only fetched when it could actually change anything (Account
  // Owner viewing a LIVE section) — hooks must stay above the early return below.
  const { data: globalSettings } = useQuery({
    queryKey: ['globalSettings'],
    queryFn: async () => {
      const { data } = await GlobalSettingsManager.getGlobalSettings()
      return data
    },
    enabled: isAccountOwner && isLiveSection,
    staleTime: GLOBAL_SETTINGS_STALE_TIME_MS
  })

  // Only the active Account Owner role sees this action.
  if (!isAccountOwner) return null

  const version = meta?.version
  const latestVersion = meta?.latestVersion
  // The backend release gate was deferred, so regenerate is always available; the revert items
  // only appear when real version history exists.
  const canRevertPrevious = meta?.previousVersion?.version != null
  const canRestoreLatest = version != null && latestVersion?.version != null && version < latestVersion.version
  // Defaults to blocked while globalSettings is still loading — matches the pre-CR-271 behavior
  // rather than briefly flashing the action enabled before the flag's real value is known.
  const isRegenerateBlocked = isLiveSection && !globalSettings?.isLiveReportsEnabled

  const renderItem = (action: ReportActionKind, label: string, disabled = false): JSX.Element => (
    <ListItem disablePadding disabled={disabled}>
      <ListItemButton disabled={disabled} onClick={() => { if (!disabled) onSelect(action) }}>
        {label}
      </ListItemButton>
    </ListItem>
  )

  return (
    <>
      {isRegenerateBlocked
        ? (
          <CustomTooltip title={REGENERATE_DISABLED_TOOLTIP}>
            <span style={{ display: 'block' }}>
              {renderItem('regenerate', 'Regenerate Report', true)}
            </span>
          </CustomTooltip>
          )
        : renderItem('regenerate', 'Regenerate Report')}
      {canRevertPrevious && renderItem('revert-previous', 'Revert to previous version')}
      {canRestoreLatest && renderItem('restore-latest', 'Restore latest version')}
    </>
  )
}

export default ReportActionsMenu
